import {
  Form as AntForm,
  FormProps,
  FormInstance,
  RowProps,
  Row,
  ColProps,
  Col,
  Space,
  Typography,
} from 'antd';
import { FieldContext } from 'rc-field-form';
import React, { useContext, useEffect, useRef } from 'react';

import styles from './Form.module.css';

type Props<Values> = FormProps<Values>;

export default function Form<Values>(props: Props<Values>) {
  const ref = useRef<FormInstance>(null);

  const initialValues = JSON.stringify(props.initialValues ?? {});

  useEffect(() => {
    ref.current?.resetFields();
  }, [initialValues]);

  // Lazy way to reinitialize Form on changes to initialValues
  return <AntForm<Values> ref={ref} {...props} />;
}

const useWatch: typeof AntForm.useWatch = (
  dependencies: Parameters<typeof AntForm.useWatch>[0],
  form: Parameters<typeof AntForm.useWatch>[1]
) => {
  const fieldContext = useContext(FieldContext);
  let updatedDependencies = dependencies;

  if (fieldContext.prefixName) {
    if (Array.isArray(updatedDependencies)) {
      updatedDependencies = [...fieldContext.prefixName, ...updatedDependencies];
    } else {
      updatedDependencies = [...fieldContext.prefixName, updatedDependencies];
    }
  }

  return AntForm.useWatch(updatedDependencies, form);
};

type FormRowProps = RowProps;

function FormRow(props: FormRowProps) {
  return <Row gutter={16} {...props} />;
}

type FormColProps = ColProps;

function FormCol(props: FormColProps) {
  return <Col {...props} />;
}

type FormLegendProps = React.ComponentProps<typeof Typography.Title>;

function FormLegend(props: FormLegendProps) {
  return <Typography.Title className={styles.legend} level={5} {...props} />;
}

type FormFooterProps = RowProps;

function FormFooter(props: FormFooterProps) {
  return <Row gutter={16} justify="space-between" {...props} />;
}

type FormFooterStartPros = ColProps;

function FormFooterStart({ children, ...props }: FormFooterStartPros) {
  return (
    <Col className={styles.footerStart} {...props}>
      <Space>{children}</Space>
    </Col>
  );
}

type FormFooterEndPros = ColProps;

function FormFooterEnd({ children, ...props }: FormFooterEndPros) {
  return (
    <Col className={styles.footerEnd} {...props}>
      <Space>{children}</Space>
    </Col>
  );
}

Form.Item = AntForm.Item;
Form.List = AntForm.List;
Form.ErrorList = AntForm.ErrorList;
Form.useForm = AntForm.useForm;
Form.useWatch = useWatch;
Form.useFormInstance = AntForm.useFormInstance;
Form.Row = FormRow;
Form.Col = FormCol;
Form.Legend = FormLegend;
Form.Footer = FormFooter;
Form.FooterStart = FormFooterStart;
Form.FooterEnd = FormFooterEnd;
